import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useSetting } from '@/hooks/useSettings'
import { EmptyNote, Section } from './shared'
import type { SearchFilters } from '@/api/types'

/** Keep the fan-out bounded: the vocabulary lists are operator-editable and one
 *  query per value is one HTTP request per value. */
const MAX_FACETS = 8

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

/**
 * Developer home. The role holds `study:read_all`, `export:create`,
 * `settings:read` and `collection:read` — and **not** `task:read`, so nothing
 * here touches `GET /tasks`. That's why the developer sections live in their own
 * file rather than behind a branch in a shared one: the query set is disjoint.
 */
export function DeveloperHome() {
  const modalities = asStringArray(useSetting('collection_modality_options')).slice(0, MAX_FACETS)
  const diseases = asStringArray(useSetting('disease_options')).slice(0, MAX_FACETS)

  return (
    <>
      <Section
        title="Query the index"
        description="Every study, 20 filter keys, CSV out. This is the surface you want."
        action={
          <Button asChild size="sm">
            <Link to="/data">Open Data</Link>
          </Button>
        }
      >
        <Card>
          <CardContent className="flex flex-wrap gap-2 p-4">
            <Button asChild variant="outline" size="sm">
              <Link to="/data?needs_tagging=true">Needs tagging</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/data?segmentation_status=finalized">Finalized masks</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/data?qc_status=ready">QC ready</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/data?has_label=true">Has a label</Link>
            </Button>
          </CardContent>
        </Card>
      </Section>

      <FacetSection
        title="Studies by modality"
        description="Exact totals — each row is the count the linked query returns."
        facetKey="modality"
        values={modalities}
        emptyCopy="No modalities are configured. Settings → Vocabulary defines the list; until then, filter modality by hand on Data."
      />

      <FacetSection
        title="Studies by disease"
        description="Declared per study at ingest, from the configured disease vocabulary."
        facetKey="disease"
        values={diseases}
        emptyCopy="No diseases are configured. Settings → Vocabulary defines the list; until then, filter disease by hand on Data."
      />

      <Section
        title="Exports"
        description="An export is a Temporal job that writes a bundle to the exports bucket."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/data">Select studies to export</Link>
          </Button>
        }
      >
        {/* ponytail: no list here on purpose. The gateway exposes POST /exports
            and GET /exports/{workflow_id} — there is no list endpoint and no
            table of workflow ids, so a "recent exports" list could only be
            invented from browser-local state. Say what's true instead. */}
        <EmptyNote>
          There's no export history to show: the gateway can start a job and report one job's
          status by id, but it has no endpoint that lists them. Start an export from a selection on
          Data — that panel polls each job until it finishes. A completed job means the bundle is
          in the <span className="font-mono">exports</span> bucket, not in your Downloads folder.
        </EmptyNote>
      </Section>
    </>
  )
}

function FacetSection({
  title,
  description,
  facetKey,
  values,
  emptyCopy,
}: {
  title: string
  description: string
  facetKey: 'modality' | 'disease'
  values: string[]
  emptyCopy: string
}) {
  return (
    <Section title={title} description={description}>
      {values.length === 0 ? (
        <EmptyNote>{emptyCopy}</EmptyNote>
      ) : (
        <Card className="divide-y">
          {values.map((v) => (
            <FacetRow key={v} facetKey={facetKey} value={v} />
          ))}
        </Card>
      )}
    </Section>
  )
}

/**
 * One facet count. `page_size=1` and read `total`: the count is the server's,
 * not a tally over a page, so it doesn't quietly become a sample as the index
 * grows.
 *
 * The key carries the page size. `/data` holds `['studies','search', filters,
 * page]` at 50 rows a page — sharing that key would hand `DataPage` a one-row
 * payload out of cache on arrival and make it render a 1-row page until its own
 * fetch landed.
 */
function FacetRow({ facetKey, value }: { facetKey: 'modality' | 'disease'; value: string }) {
  const filters: SearchFilters = { [facetKey]: value }
  const { data, isLoading } = useQuery({
    queryKey: ['studies', 'search', filters, 1, 1],
    queryFn: () => apiClient.searchStudies(filters, 1, 1),
    staleTime: 60_000,
  })

  return (
    <Link
      to={`/data?${facetKey}=${encodeURIComponent(value)}`}
      className="flex items-center justify-between gap-3 p-3 transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="min-w-0 truncate text-sm">{value}</span>
      {isLoading ? (
        <Skeleton className="h-5 w-10" />
      ) : (
        <span className="shrink-0 font-mono text-sm tabular-nums">{data?.total ?? 0}</span>
      )}
    </Link>
  )
}
